'use client';

import {useCallback, useEffect, useMemo} from 'react';
import Cookies from 'js-cookie';
import {useRouter, usePathname} from 'next/navigation';
import {useAppActivation, useInterval} from '@/utils/hooks';
import {fingerprintStore} from '@/utils/fingerprints/FingerprintStore';
import getGameId from '@/utils/getGameId';
import {getCurrentGameCookieName} from '@/utils/constant';
import {getEnvConfig} from '@/utils/getEnvConfig';
import auth from '@/services/internal/auth/AuthQueue';
import cleanupUserCache from '@/utils/cleanupUserCache';
import {liveEventSignupUrl, signupUrl} from '@/utils/signup';

export default function AuthHandler() {
  const router = useRouter();
  const path = usePathname();

  const isRegistering = useMemo(() => {
    const gameId = getGameId();
    return (
      path.startsWith(`/games/${gameId}/${liveEventSignupUrl}`) ||
      path.startsWith(`/games/${gameId}/${signupUrl}`) ||
      path.startsWith(`/games/${gameId}/tutorial`)
    );
  }, [path]);

  const returnToSignInScreen = useCallback(() => {
    const gameId = getGameId();
    Cookies.remove(getCurrentGameCookieName(gameId));
    router.push(`/games/${gameId}/`);
  }, [router]);

  const refreshAuth = useCallback(async () => {
    const gameId = getGameId();
    const isAuthenticated = Boolean(Cookies.get(getEnvConfig().CURRENT_USER_COOKIE_NAME));
    const unityConfig = fingerprintStore.getUnityConfig(gameId);

    if (!navigator.onLine || !unityConfig || isRegistering) {
      return;
    }

    if (!isAuthenticated) {
      return returnToSignInScreen();
    }

    auth
      .refreshToken()
      .then((resp) => {
        if (!resp.ok) {
          returnToSignInScreen();
        }
      })
      .catch((error) => {
        console.log(error);
        returnToSignInScreen();
      });
  }, [isRegistering, returnToSignInScreen]);

  const isActive = useAppActivation(refreshAuth);

  useInterval(refreshAuth, isActive ? 60_000 : null);

  useEffect(() => {
    cleanupUserCache();
  }, []);

  return null;
}
